import { Instance, types, getRoot } from "mobx-state-tree";
import {
  Font,
  ProjectVersion,
  UpdateProjectInput,
  UpdateProjectVersionInput,
} from "../API";
import { generateClient } from "aws-amplify/api";
import { getProject, getSystemData } from "../jsxProviders/scripts/jsxTools";
import { insertIntoGraphQlString } from "modifygraphqlstring";
import { getAccessTokenForLambdaAuth } from "../utils/authHelpers";
import { listProjectsByAccount, listProjectVersionsByAccount } from "../graphql/queries";
import type { RootInstance } from "./root";

import Observable from "zen-observable";
import {
  asyncOpStateType,
  updateAsyncStatus,
  AsyncOpState,
  saveCompProcessStepType,
} from "./utils/async-op-state";
import { createProject, updateProjectVersion } from "../graphql/mutations";
import { onUpdateProjectVersionSub } from "../graphql/subscriptions";
import { rootStore } from "./root";
import {
  createVersion,
  getLatestProjectVersion,
  getPublishedProject,
} from "./utils/projectTools";
import { getLatestPlayerVersion } from "./utils/getLatestPlayerVersion";
import { projectTitleToAlias } from "./utils/projectNameToAlias";
import { EXTENSION_SOURCE } from "./utils/consts";
export type ProjectBasicInfo = {
  title: string;
  id: string;
  updatedAt: string;
  projectAccountId: string;
  draftUpdatedAt: string;
};
const amplifyClient = generateClient();
export const ProjectStore = types
  .model({
    selectedProjectId: types.optional(types.string, ""),
    allProjects: types.optional(
      types.array(types.frozen<ProjectBasicInfo>()),
      []
    ),
    projectWorkspaceVersion: types.maybeNull(
      types.frozen<UpdateProjectVersionInput>()
    ),
    saveCompStatus: asyncOpStateType,
    saveCompStep: saveCompProcessStepType,
    addNewVidPartStatus: asyncOpStateType,
    noProjects: types.optional(types.boolean, false),
    AEVersion: types.optional(types.string, ""),
    isLoading: types.optional(types.boolean, true),
  })
  .views((self) => ({
    get selectedProject() {
      return self.allProjects.find((p) => p.id === self.selectedProjectId);
    },
    get platformStorageProjectAssetsRoot() {
      return `projects/${self.selectedProjectId}/assets`;
    },
    get videoParts() {
      return self.projectWorkspaceVersion?.videoParts?.map((vp) => ({
        name: vp.name,
        jsonUrl: vp.jsonUrl,
        updatedAt: vp?.updatedAt,
        hasVideo: vp?.hasVideo,
      }));
    },
  }))
  .actions((self) => ({
    setIsLoading(bool: boolean) {
      self.isLoading = bool;
    },
    selectedSceneExists(selectedCompName: string) {
      return self.projectWorkspaceVersion?.videoParts?.find(
        (v) => v.name === selectedCompName
      );
    },
    setAEVersion(version: string) {
      self.AEVersion = version;
    },
  }))
  .actions((self) => ({
    setSelectedProjectId(pId: string) {
      self.selectedProjectId = pId;
    },
    updateAddNewVideoPartStatus: updateAsyncStatus(self, "addNewVidPartStatus"),
    setWorkspaceProject(project: ProjectVersion | null) {
      const cleanVideoparts = project?.videoParts?.map((vp) => {
        const { __typename, ...rest } = vp;
        return rest;
      });
      const cleanProject = project
        ? {
            ...project,
            videoParts: cleanVideoparts,
          }
        : null;
      self.projectWorkspaceVersion = cleanProject;
    },
    setProjects(projects: ProjectBasicInfo[]) {
      self.allProjects.replace(projects);
    },

    setNoProject(flag: boolean) {
      self.noProjects = flag;
    },
  }))
  .actions((self) => {
    let subscription: ZenObservable.Subscription;
    return {
      async setSelectedProject(pid: UpdateProjectInput["id"]) {
        let latestProjectVersion = await getLatestProjectVersion(pid);

        if (!latestProjectVersion) {
          // if there is no workspace version, create one out of the published project
          const publishedProject = await getPublishedProject(pid);
          latestProjectVersion = await createVersion(publishedProject);
        }
        self.setWorkspaceProject(latestProjectVersion);
        self.setSelectedProjectId(pid);
        subscription?.unsubscribe();
        const modifiedSubscriptionString = insertIntoGraphQlString(
          insertIntoGraphQlString(onUpdateProjectVersionSub, {
            path: ["videoParts"],
            key: "modsArr",
            value: {
              id: true,
              dataStr: true,
              origin: true,
              name: true,
              description:true
            },
          }),
          {
            path: ["videoParts"],
            key: "fonts",
            value: {
              family: true,
              style: true,
              url: true,
              weight: true,
            },
          }
        );
        const authToken = await getAccessTokenForLambdaAuth();
        const response = amplifyClient.graphql({
          query: modifiedSubscriptionString,
          variables: {
            id: pid,
          },
          authMode: "lambda",
          authToken,
        });

        if (response instanceof Observable) {
          subscription = response.subscribe({
            next: (value) => {
              if (value.errors) return console.error(value.errors);
              const versionInDb = value.value.data
                .onUpdateProjectVersionSub as ProjectVersion;
              if (versionInDb?.videoParts) {
                self.setWorkspaceProject(versionInDb);
              }
            },
            error: (errorValue) => {
              console.error(errorValue);
              subscription.unsubscribe();
            },
          });
        }
      },
    };
  })
  .actions((self) => {
    return {
      async loadProjects() {
        self.setIsLoading(true);

        // Get all account IDs from the auth store
        const root = getRoot<RootInstance>(self);
        
        // Ensure accounts are loaded
        if (!root.authStore.accountsLoaded) {
          console.log("Accounts not loaded yet. Loading accounts first...");
          await root.authStore.loadAccountData();
        }

        const accountIds = root.authStore.accountIds;
        
        if (!accountIds || accountIds.length === 0) {
          console.error("No account IDs found. Cannot load projects.");
          self.setIsLoading(false);
          return;
        }

        console.log(`Loading projects from ${accountIds.length} accounts:`, accountIds);

        // Get basic info from all projects (published) by account
        const listProjectVersionBasic = `query ListProjectVersionsByAccount(
          $accountOwner: String!
          $sortDirection: ModelSortDirection
          $filter: ModelProjectVersionFilterInput
          $limit: Int
          $nextToken: String
        ) {
          listProjectVersionsByAccount(
            accountOwner: $accountOwner
            sortDirection: $sortDirection
            filter: $filter
            limit: $limit
            nextToken: $nextToken
          ) {
            items {
              id
              updatedAt
            }
            nextToken
          }
        }`;

        const listProjectBasicInfo = `query ListProjectsByAccount(
          $projectAccountId: ID!
          $sortDirection: ModelSortDirection
          $filter: ModelProjectFilterInput
          $limit: Int
          $nextToken: String
        ) {
          listProjectsByAccount(
            projectAccountId: $projectAccountId
            sortDirection: $sortDirection
            filter: $filter
            limit: $limit
            nextToken: $nextToken
          ) {
            items {
              id
              title
              updatedAt
              projectAccountId
            }
            nextToken
          }
        }`;

        const allProjects: ProjectBasicInfo[] = [];
        const authToken = await getAccessTokenForLambdaAuth();

        // Fetch projects from all accounts
        for (const accountId of accountIds) {
          console.log(`Fetching projects for account: ${accountId}`);
          let nextToken = null;
          const accountProjects: ProjectBasicInfo[] = [];
          
          // Get published projects
          do {
            const projectsData = (await amplifyClient.graphql({
              query: listProjectBasicInfo,
              variables: {
                projectAccountId: accountId,
                sortDirection: "DESC",
                nextToken
              },
              authMode: "lambda",
              authToken,
            })) as { data: any };
            nextToken = projectsData?.data?.listProjectsByAccount?.nextToken;
            if (projectsData?.data?.listProjectsByAccount?.items) {
              accountProjects.push(...projectsData.data.listProjectsByAccount.items);
            }
          } while (nextToken);
          
          console.log(`Found ${accountProjects.length} published projects for account: ${accountId}`);
          
          // Get the draft versions of the projects and update with draft updatedAt
          nextToken = null;
          do {
            const projectsData = (await amplifyClient.graphql({
              query: listProjectVersionBasic,
              variables: {
                accountOwner: accountId,
                sortDirection: "DESC",
                nextToken
              },
              authMode: "lambda",
              authToken,
            })) as { data: any };
            nextToken = projectsData?.data?.listProjectVersionsByAccount?.nextToken;
            if (projectsData?.data?.listProjectVersionsByAccount?.items) {
              for (const draft of projectsData.data.listProjectVersionsByAccount.items) {
                const publishedProjectIndex = accountProjects.findIndex(
                  (p) => p.id === draft.id
                );
                if (publishedProjectIndex >= 0) {
                  accountProjects[publishedProjectIndex] = {
                    ...accountProjects[publishedProjectIndex],
                    draftUpdatedAt: draft?.updatedAt,
                  };
                }
              }
            }
          } while (nextToken);
          
          allProjects.push(...accountProjects);
        }

        console.log(`Total projects loaded from all accounts: ${allProjects.length}`);

        console.log(`Total projects loaded from all accounts: ${allProjects.length}`);

        if (allProjects.length) {
          self.setNoProject(false);
          self.setProjects(allProjects.filter((p) => !!p.id));
        } else {
          self.setNoProject(true);
        }
        if (self.selectedProjectId) {
          await self.setSelectedProject(self.selectedProjectId);
        }
        self.setIsLoading(false);
      },
      async updateProjectVideoPart(
        videoPartName: string,
        hasVideo?: boolean,
        jsonVidUrl?: string,
        fonts?: Font[]
      ) {
        videoPartName = videoPartName.trim();
        self.updateAddNewVideoPartStatus(AsyncOpState.Saving);
        await self.setSelectedProject(self.selectedProjectId);

        const vp = self.projectWorkspaceVersion?.videoParts?.find(
          (vp) => vp.name === videoPartName
        );
        let playerVersion = null;
        try {
          // Get the latest minor version of the player
          playerVersion = await getLatestPlayerVersion();
        } catch (e) {
          console.error(e);
        }
        let vps;
        if (vp) vp.hasVideo = hasVideo;
        if (vp && jsonVidUrl) {
          vp.jsonUrl = jsonVidUrl;
          vp.fonts = fonts;
          vp.updatedAt = new Date().toISOString();
          vp.origin = EXTENSION_SOURCE;
          vp.playerVersionToUse = playerVersion;
          vps = self.projectWorkspaceVersion?.videoParts;
        } else if (vp && !jsonVidUrl) {
          rootStore.uiStore.setAlertMessage(
            `Scene with the name ${videoPartName} already exist, please select a different name`
          );
          rootStore.uiStore.setAlertModalVisible();
          return;
        } else {
          vps = [
            ...(self.projectWorkspaceVersion?.videoParts || []),
            {
              name: videoPartName,
              jsonUrl: jsonVidUrl ? jsonVidUrl : "",
              modsArr: [],
              fonts: [],
              updatedAt: new Date().toISOString(),
              origin: EXTENSION_SOURCE,
              playerVersionToUse: playerVersion,
              description: vp?.description || "",
            },
          ];
        }

        try {
          const modifiedSub = insertIntoGraphQlString(
            insertIntoGraphQlString(updateProjectVersion, {
              path: ["videoParts"],
              key: "modsArr",
              value: {
                id: true,
                dataStr: true,
                origin: true,
                name: true,
                description:true
              },
            }),
            {
              path: ["videoParts"],
              key: "fonts",
              value: {
                family: true,
                style: true,
                url: true,
                weight: true,
              },
            }
          );
          const authToken = await getAccessTokenForLambdaAuth();
          const res = await amplifyClient.graphql({
            query: modifiedSub,
            variables: {
              input: {
                id: self.selectedProjectId,
                videoParts: vps,
                ...(playerVersion && { playerVersionToUse: playerVersion }),
              },
            },
            authMode: "lambda",
            authToken,
          });
          self.updateAddNewVideoPartStatus(AsyncOpState.Success);
          await self.setSelectedProject(self.selectedProjectId);
          return res;
        } catch (e) {
          const currentComposition =
            rootStore.compositionStore.compositionCurrentlyRendering;
          console.error("err", e);
          self.updateAddNewVideoPartStatus(AsyncOpState.Error);
          rootStore.uiStore.setProblemLayersData(
            {
              UnknownError: [
                `Unknown Error Occurred in ${currentComposition.name}`,
              ],
            },
            currentComposition.name
          );
          rootStore.compositionStore.setRenderedCompositions(
            currentComposition.id,
            false
          );
          throw new Error("Failed to save video part");
        }
      },
    };
  })
  .actions((self) => ({
    async createNewProject(name: string, groupId: string) {
      const defaultSchema = JSON.stringify({
        $schema: "http://json-schema.org/draft-07/schema",
        $id: "http://example.com/example.json",
        type: "object",
        title: "",
        description: "",
        default: {},
        examples: [{}],
        required: [],
        properties: {},
        additionalProperties: true,
      });
      const aliasId = projectTitleToAlias(name);
      try {
        if (name.length) {
          const authToken = await getAccessTokenForLambdaAuth();
          const res = (await amplifyClient.graphql({
            query: createProject,
            variables: {
              input: {
                title: name.trim(),
                aliasId,
                projectAccountId: groupId,
                stateJsonSchemaStr: defaultSchema,
                settingsJsonSchemaStr: defaultSchema,
                allowCreateDataPoint: true,
                allowDataConnect: true,
                analyticsEnabled: true,
              },
            },
            authMode: "lambda",
            authToken,
          })) as { data: any };
          const id = res?.data?.createProject?.id;
          await createVersion(res?.data?.createProject);
          // this will update the allProjects to update the projectSelect
          await self.loadProjects();
          id && self.setSelectedProject(id);
          return id;
        }
      } catch (e) {
        console.log(e);
      }
    },
    async getAEVersion() {
      try {
        const data: any = await getSystemData();
        self.setAEVersion(data.AEVersion);
      } catch (error) {
        console.log(error);
      }
    },
  }));

export type IProjectModel = Instance<typeof ProjectStore>;
